import { useEffect, useState } from 'react'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { zohoSettingsDefaultValues, ZohoSettingsFormValues } from './models'
import { Controller, useForm } from 'react-hook-form'
import { useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Button } from '../../../../../components/Button/Button'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'

export const ZohoContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'zoho',
    direction: 'inbound',
    object: 'contact_operation',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const form = useForm<ZohoSettingsFormValues>({ defaultValues: zohoSettingsDefaultValues })
  const [, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'zoho')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  if (isGetCheckpointFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  const onSubmit = async (data: ZohoSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and Zoho.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
            >
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
